import React, { useEffect, useMemo, useState } from 'react';

const styles = {
  shell: {
    maxWidth: 980,
    margin: '0 auto',
    padding: '32px 16px',
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
  },
  card: {
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid #e2e8f0',
    borderRadius: 24,
    boxShadow: '0 18px 60px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
  },
  hero: {
    padding: '32px 32px 20px',
    background: 'linear-gradient(135deg, #0f766e 0%, #164e63 100%)',
    color: '#fff',
  },
  title: {
    margin: 0,
    fontSize: 34,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  subtitle: {
    margin: '10px 0 0',
    maxWidth: 720,
    color: 'rgba(255,255,255,0.88)',
    lineHeight: 1.6,
    fontSize: 15,
  },
  content: {
    padding: 32,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 18,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 18,
  },
  full: {
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1e293b',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    fontSize: 15,
    borderRadius: 14,
    border: '1px solid #cbd5e1',
    outline: 'none',
    background: '#fff',
    transition: 'border-color .2s ease, box-shadow .2s ease',
  },
  textarea: {
    minHeight: 120,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  helper: {
    fontSize: 12,
    color: '#64748b',
  },
  error: {
    fontSize: 13,
    color: '#dc2626',
  },
  alertSuccess: {
    marginBottom: 18,
    padding: 18,
    borderRadius: 14,
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#166534',
  },
  button: {
    border: 'none',
    borderRadius: 14,
    background: '#0f766e',
    color: '#fff',
    padding: '16px 22px',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    minWidth: 220,
  },
  jobsWrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
    margin: '0 0 28px',
  },
  jobCard: {
    border: '1px solid #dbeafe',
    background: '#f8fbff',
    borderRadius: 16,
    padding: 16,
  },
  jobTitle: {
    margin: '0 0 6px',
    fontSize: 16,
    fontWeight: 700,
  },
  muted: {
    margin: 0,
    color: '#64748b',
    fontSize: 14,
    lineHeight: 1.5,
  },
};

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('career-form-styles')) return;

  const style = document.createElement('style');
  style.id = 'career-form-styles';
  style.textContent = `
    .career-input:focus, .career-textarea:focus, .career-select:focus {
      border-color: #0f766e !important;
      box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12) !important;
    }
    .career-submit:hover {
      background: #115e59 !important;
    }
    .career-submit:disabled {
      cursor: not-allowed !important;
      background: #99b7b3 !important;
    }
    @media (max-width: 768px) {
      .career-grid {
        grid-template-columns: 1fr !important;
      }
      .career-card-content {
        padding: 22px !important;
      }
      .career-hero {
        padding: 24px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function endpoint(baseUrl, path) {
  return `${String(baseUrl).replace(/\/$/, '')}${path}`;
}

async function parseJson(response) {
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || 'Unexpected server response.' };
  }
}

const initialForm = {
  job_id: '',
  full_name: '',
  email: '',
  phone: '',
  position_applied_for: '',
  years_of_experience: '',
  current_company: '',
  academic_details: '',
  skills: '',
  cover_letter: '',
};

export function Career({
  apiBaseUrl = 'http://localhost:8000/api',
  title = 'Join Our Team',
  subtitle = 'Submit your application with your academic background, experience, preferred position, and resume. PDF and Word files are supported.',
  showJobs = true,
  onSuccess,
}) {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    let active = true;

    async function loadJobs() {
      setLoadingJobs(true);

      try {
        const response = await fetch(endpoint(apiBaseUrl, '/jobs/public'), {
          headers: { Accept: 'application/json' },
        });
        const payload = await parseJson(response);

        if (!response.ok) {
          throw new Error(payload.message || 'Unable to load career positions.');
        }

        if (active) {
          setJobs(Array.isArray(payload.data) ? payload.data.filter((job) => job.is_active !== false) : []);
        }
      } catch {
        if (active) {
          setJobs([]);
        }
      } finally {
        if (active) {
          setLoadingJobs(false);
        }
      }
    }

    loadJobs();

    return () => {
      active = false;
    };
  }, [apiBaseUrl]);

  const selectedJob = useMemo(
    () => jobs.find((job) => String(job.id) === String(form.job_id)) || null,
    [jobs, form.job_id],
  );

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  }

  function handleJobChange(value) {
    const job = jobs.find((item) => String(item.id) === String(value));

    setForm((current) => ({
      ...current,
      job_id: value,
      position_applied_for: job?.title || current.position_applied_for,
    }));

    setErrors((current) => ({
      ...current,
      job_id: '',
      position_applied_for: '',
    }));
  }

  function validateForm() {
    const nextErrors = {};

    if (!form.full_name.trim()) nextErrors.full_name = 'Full name is required.';
    if (!form.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!form.position_applied_for.trim()) nextErrors.position_applied_for = 'Position applied for is required.';
    if (!form.academic_details.trim()) nextErrors.academic_details = 'Academic details are required.';

    if (form.years_of_experience !== '' && Number(form.years_of_experience) < 0) {
      nextErrors.years_of_experience = 'Experience cannot be negative.';
    }

    if (!resume) {
      nextErrors.resume = 'Resume file is required.';
    } else {
      const fileName = resume.name.toLowerCase();
      const validExtension = ['.pdf', '.doc', '.docx'].some((ext) => fileName.endsWith(ext));

      if (!validExtension) {
        nextErrors.resume = 'Upload a PDF, DOC, or DOCX file.';
      } else if (resume.size > 10 * 1024 * 1024) {
        nextErrors.resume = 'Resume must be 10 MB or smaller.';
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  function handleSubmit(event) {
    event.preventDefault();
    setSuccessMessage('');

    if (!validateForm()) return;

    const submittedApplication = { ...form, resume };
    setForm(initialForm);
    setResume(null);
    setErrors({});
    setSuccessMessage('Thank you, we will get back to you soon.');

    if (typeof document !== 'undefined') {
      const fileInput = document.getElementById('career-resume-input');
      if (fileInput) fileInput.value = '';
    }

    onSuccess?.(submittedApplication);
  }

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <div style={styles.hero} className="career-hero">
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>

        <div style={styles.content} className="career-card-content">
          {showJobs && (
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 20 }}>Open Positions</h3>
              {loadingJobs ? (
                <p style={styles.muted}>Loading open positions...</p>
              ) : jobs.length > 0 ? (
                <div style={styles.jobsWrap}>
                  {jobs.map((job) => (
                    <div key={job.id} style={styles.jobCard}>
                      <h4 style={styles.jobTitle}>{job.title}</h4>
                      <p style={styles.muted}>{job.department || 'General'}</p>
                      <p style={{ ...styles.muted, marginTop: 8 }}>{job.location || 'Location not specified'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={styles.muted}>No positions were returned from the API, but applicants can still submit the form manually.</p>
              )}
            </div>
          )}

          {successMessage && <div style={styles.alertSuccess} role="status" aria-live="polite">{successMessage}</div>}

          <form onSubmit={handleSubmit}>
            <div style={styles.grid} className="career-grid">
              <div style={styles.field}>
                <label style={styles.label}>Select Position</label>
                <select
                  className="career-select"
                  style={styles.input}
                  value={form.job_id}
                  onChange={(event) => handleJobChange(event.target.value)}
                >
                  <option value="">Choose an opening</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Position Applied For</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="text"
                  value={form.position_applied_for}
                  onChange={(event) => updateField('position_applied_for', event.target.value)}
                  placeholder="Enter the role you are applying for"
                />
                {errors.position_applied_for && <div style={styles.error}>{errors.position_applied_for}</div>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Full Name</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="text"
                  value={form.full_name}
                  onChange={(event) => updateField('full_name', event.target.value)}
                  placeholder="Enter your full name"
                />
                {errors.full_name && <div style={styles.error}>{errors.full_name}</div>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Email Address</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  placeholder="Enter your email"
                />
                {errors.email && <div style={styles.error}>{errors.email}</div>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Phone Number</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="tel"
                  value={form.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder="Enter your phone number"
                />
                {errors.phone && <div style={styles.error}>{errors.phone}</div>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Years of Experience</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="number"
                  min="0"
                  value={form.years_of_experience}
                  onChange={(event) => updateField('years_of_experience', event.target.value)}
                  placeholder="e.g. 4"
                />
                {errors.years_of_experience && <div style={styles.error}>{errors.years_of_experience}</div>}
              </div>

              <div style={{ ...styles.field, ...styles.full }}>
                <label style={styles.label}>Current Company</label>
                <input
                  className="career-input"
                  style={styles.input}
                  type="text"
                  value={form.current_company}
                  onChange={(event) => updateField('current_company', event.target.value)}
                  placeholder="Current or most recent organization"
                />
              </div>

              <div style={{ ...styles.field, ...styles.full }}>
                <label style={styles.label}>Academic Details</label>
                <textarea
                  className="career-textarea"
                  style={{ ...styles.input, ...styles.textarea }}
                  value={form.academic_details}
                  onChange={(event) => updateField('academic_details', event.target.value)}
                  placeholder="Degree, institution, passing year, specialization, certifications, and any other academic information"
                />
                {errors.academic_details && <div style={styles.error}>{errors.academic_details}</div>}
              </div>

              <div style={{ ...styles.field, ...styles.full }}>
                <label style={styles.label}>Skills</label>
                <textarea
                  className="career-textarea"
                  style={{ ...styles.input, ...styles.textarea }}
                  value={form.skills}
                  onChange={(event) => updateField('skills', event.target.value)}
                  placeholder="List your relevant skills, tools, and strengths"
                />
              </div>

              <div style={{ ...styles.field, ...styles.full }}>
                <label style={styles.label}>Cover Letter / Additional Notes</label>
                <textarea
                  className="career-textarea"
                  style={{ ...styles.input, ...styles.textarea }}
                  value={form.cover_letter}
                  onChange={(event) => updateField('cover_letter', event.target.value)}
                  placeholder="Tell us why you are a strong fit for this role"
                />
              </div>

              <div style={{ ...styles.field, ...styles.full }}>
                <label style={styles.label}>Upload Resume</label>
                <input
                  id="career-resume-input"
                  style={styles.input}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setResume(file);
                    setErrors((current) => ({ ...current, resume: '' }));
                  }}
                />
                <div style={styles.helper}>Accepted formats: PDF, DOC, DOCX. Maximum size: 10 MB.</div>
                {resume && <div style={styles.helper}>Selected file: {resume.name}</div>}
                {selectedJob?.application_deadline && (
                  <div style={styles.helper}>Application deadline: {selectedJob.application_deadline}</div>
                )}
                {errors.resume && <div style={styles.error}>{errors.resume}</div>}
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <button className="career-submit" type="submit" style={styles.button}>
                Submit Application
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Career;
